import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import { Plus, Edit2, Trash2, Search, Coffee, Tag } from 'lucide-react';

const AdminBarCatalog = () => {
    const { slug } = useParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        price: '',
        category: 'Bebida',
        isCombo: false
    });

    useEffect(() => {
        fetchProducts();
    }, [slug]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`/${slug}/api/products`);
            setProducts(data);
        } catch (err) {
            setError('Error al cargar el catálogo.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await axios.put(`/${slug}/api/products/${isEditing}`, formData);
            } else {
                await axios.post(`/${slug}/api/products`, formData);
            }
            setIsModalOpen(false);
            fetchProducts();
        } catch (err) {
            alert('Error al guardar el producto.');
        }
    };

    const handleEdit = (product) => {
        setFormData({
            name: product.name,
            price: product.price,
            category: product.category || 'Bebida',
            isCombo: product.isCombo || false
        });
        setIsEditing(product._id);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Seguro que querés eliminar este producto?')) return;
        try {
            await axios.delete(`/${slug}/api/products/${id}`);
            fetchProducts();
        } catch (err) {
            alert('Error al eliminar.');
        }
    };

    const openForm = () => {
        setFormData({ name: '', price: '', category: 'Bebida', isCombo: false });
        setIsEditing(null);
        setIsModalOpen(true);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl animate-fade-in-up">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <div className="flex items-center gap-3">
                        <Coffee className="text-primary-orange" size={28} />
                        <h1 className="text-4xl font-black text-white px-2">Catálogo de Barra</h1>
                    </div>
                    <p className="text-white/40 font-medium px-2 mt-2">Gestioná las bebidas y combos para todos tus eventos.</p>
                </div>
                <button
                    onClick={openForm}
                    className="flex items-center gap-2 bg-brand-gradient px-6 py-3 rounded-2xl font-black shadow-lg shadow-orange-500/20 hover:scale-105 active:scale-95 transition-all w-max"
                >
                    <Plus size={20} />
                    NUEVO PRODUCTO
                </button>
            </div>

            <div className="bg-glass rounded-[40px] border-white/5 shadow-2xl overflow-hidden p-6 md:p-8">
                {products.length === 0 ? (
                    <div className="text-center py-16 text-white/40">
                        <Tag size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-bold">No hay productos en el catálogo</p>
                        <p className="text-sm">Agregá tu primera bebida para empezar a vender.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {products.map(product => (
                            <div key={product._id} className="bg-white/5 border border-white/5 rounded-3xl p-6 group hover:border-white/20 transition-all flex flex-col">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <span className="text-[10px] font-black uppercase tracking-[3px] text-primary-orange mb-1 bg-primary-orange/10 px-2 py-1 rounded inline-block">
                                            {product.category}
                                        </span>
                                        <h3 className="text-xl font-black text-white leading-tight mt-2">{product.name}</h3>
                                        {product.isCombo && <span className="text-xs text-blue-400 font-bold ml-0 mt-1 block">★ Combo Especial</span>}
                                    </div>
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEdit(product)} className="p-2 bg-white/10 rounded-xl hover:bg-white/20 hover:text-white text-white/50 transition-colors">
                                            <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(product._id)} className="p-2 bg-red-500/10 rounded-xl hover:bg-red-500/20 hover:text-red-400 text-red-500/50 transition-colors">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-auto pt-4 border-t border-white/5">
                                    <p className="text-3xl font-black text-white tracking-tight">${product.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <div className="bg-[#121212] border border-white/10 rounded-[32px] w-full max-w-md p-8 shadow-2xl">
                        <h2 className="text-2xl font-black text-white mb-6">
                            {isEditing ? 'Editar Producto' : 'Crear Producto'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Nombre</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-primary-orange transition-colors"
                                    placeholder="Ej: Fernet Branca 750ml"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Precio de Venta ($)</label>
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-primary-orange transition-colors"
                                    placeholder="Ej: 15000"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Categoría</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary-orange transition-colors"
                                >
                                    <option value="Bebida">Bebida</option>
                                    <option value="Comida">Comida</option>
                                    <option value="Merchandising">Merchandising</option>
                                    <option value="Varios">Varios</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-3 py-2 cursor-pointer pt-4">
                                <input
                                    type="checkbox"
                                    name="isCombo"
                                    id="isCombo"
                                    checked={formData.isCombo}
                                    onChange={handleInputChange}
                                    className="w-5 h-5 accent-primary-orange rounded bg-white/10 border-white/20"
                                />
                                <label htmlFor="isCombo" className="text-sm font-bold text-white cursor-pointer select-none">
                                    Es un combo / promoción
                                </label>
                            </div>

                            <div className="flex gap-4 pt-6">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 px-4 py-3 bg-white/5 rounded-xl font-bold text-white/60 hover:text-white hover:bg-white/10 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 px-4 py-3 bg-brand-gradient rounded-xl font-bold text-white shadow-lg shadow-orange-500/20 active:scale-95 transition-all">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBarCatalog;
